import React, { useState, useEffect } from "react";
import api from "../services/api";
import io from "socket.io-client";
import DeviceHistory from "./DeviceHistory";
import GaugePanel from "./GaugePanel";
import GlobalHistory from "./GlobalHistory";
import GlobalControlPanel from "./GlobalControlPanel";
import CriticalAlerts from "./CriticalAlerts";
import MetricsView from "./MetricsView";

const Dashboard = ({ user, showAlertsOnly, viewPreferences = {}, commandPreferences = {} }) => {
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSites, setExpandedSites] = useState({});
  const [viewMode, setViewMode] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [showControls, setShowControls] = useState(false);
  const [theme, setTheme] = useState("dark"); // "dark" o "light"

  // Colores del tema
  const themeColors = theme === "dark" ? {
    // Modo oscuro (actual)
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
    cardBg: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
    headerBg: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
    siteHeaderBg: "linear-gradient(90deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)",
    text: "#ffffff",
    textSecondary: "rgba(255,255,255,0.6)",
    textMuted: "rgba(255,255,255,0.4)",
    border: "rgba(255,255,255,0.1)",
    inputBg: "rgba(255,255,255,0.08)",
    buttonBg: "rgba(255,255,255,0.05)",
    onlineGradient: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 78, 59, 0.2) 100%)",
    offlineGradient: "linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(127, 29, 29, 0.35) 100%)",
    onlineRowGradient: "linear-gradient(90deg, rgba(16, 185, 129, 0.08) 0%, rgba(6, 78, 59, 0.12) 100%)",
    offlineRowGradient: "linear-gradient(90deg, rgba(239, 68, 68, 0.2) 0%, rgba(127, 29, 29, 0.25) 100%)",
    shadow: "0 8px 32px rgba(0,0,0,0.3)",
    valueBg: "rgba(255,255,255,0.05)"
  } : {
    // Modo claro
    background: "linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 50%, #f0f4f8 100%)",
    cardBg: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)",
    headerBg: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 100%)",
    siteHeaderBg: "linear-gradient(90deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)",
    text: "#1e293b",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "rgba(0,0,0,0.1)",
    inputBg: "rgba(0,0,0,0.05)",
    buttonBg: "rgba(0,0,0,0.05)",
    onlineGradient: "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)",
    offlineGradient: "linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.15) 100%)",
    onlineRowGradient: "linear-gradient(90deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
    offlineRowGradient: "linear-gradient(90deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)",
    shadow: "0 8px 32px rgba(0,0,0,0.1)",
    valueBg: "rgba(0,0,0,0.05)"
  };

  // Estados para modales
  const [showHistory, setShowHistory] = useState(false);
  const [showGlobalHistory, setShowGlobalHistory] = useState(false);
  const [showGaugePanel, setShowGaugePanel] = useState(false);
  const [showGlobalControlPanel, setShowGlobalControlPanel] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    fetchDashboardData();

    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const socket = io(apiUrl, { auth: { token: localStorage.getItem('token') } });

    socket.on("connect", () => {
      console.log("✅ Dashboard: Connected to WebSocket at", apiUrl);
    });

    socket.on("device-update", (data) => {
      const { deviceId, deviceIdMqtt, magnitudes, currentState, status, lastSeen } = data;

      setDevices(prevDevices => {
        return prevDevices.map(device => {
          const isMatch = device._id === deviceId ||
                          device._id === String(deviceId) ||
                          device.deviceId === deviceIdMqtt;

          if (isMatch) {
            const updatedDevice = {
              ...device,
              status: status || device.status,
              lastSeen: lastSeen || device.lastSeen
            };

            if (currentState) {
              updatedDevice.currentState = { ...device.currentState, ...currentState };
            }

            if (magnitudes && Array.isArray(magnitudes)) {
              updatedDevice.magnitudes = magnitudes;
            }

            return updatedDevice;
          }
          return device;
        });
      });
    });

    const interval = setInterval(() => {
      fetchDashboardData();
    }, 60000);

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [devicesRes, sitesRes] = await Promise.all([
        api.get("/api/devices"),
        api.get("/api/sites")
      ]);

      const devicesData = Array.isArray(devicesRes.data) ? devicesRes.data : [];
      const sitesData = Array.isArray(sitesRes.data) ? sitesRes.data : [];

      setDevices(devicesData);
      setSites(sitesData);

      const expanded = {};
      sitesData.forEach(site => {
        expanded[site._id] = true;
      });
      setExpandedSites(expanded);
    } catch (error) {
      console.error("❌ Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSiteExpansion = (siteId) => {
    setExpandedSites(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  const expandAll = () => {
    const expanded = {};
    sites.forEach(site => {
      expanded[site._id] = true;
    });
    setExpandedSites(expanded);
  };

  const collapseAll = () => {
    setExpandedSites({});
  };

  const openHistory = (deviceId) => {
    // Abrir el historial en una pestaña nueva (página standalone)
    window.open(`/device-history/${deviceId}`, '_blank');
  };

  const openGaugePanel = (device) => {
    setSelectedDevice(device);
    setShowGaugePanel(true);
  };

  const sendSiteStatusToTelegram = async (siteId, siteName, e) => {
    e.stopPropagation(); // Evitar que se expanda/colapse el sitio
    try {
      const response = await api.post(`/api/sites/${siteId}/send-status`);
      alert(`✅ Estado de "${siteName}" enviado a Telegram`);
    } catch (error) {
      console.error("Error enviando estado:", error);
      const message = error.response?.data?.message || "Error al enviar estado";
      alert(`❌ ${message}`);
    }
  };

  const getDevicesBysite = (siteId) => {
    if (!Array.isArray(devices)) return [];

    const siteDevices = devices.filter(d => d.site?._id === siteId || d.siteId === siteId);

    if (!user || user.role === "admin") {
      return siteDevices;
    }

    if (!user.devicePermissions) {
      return [];
    }

    return siteDevices.filter(device => {
      const deviceId = device._id;
      const devicePermissions = user.devicePermissions[deviceId];
      return devicePermissions && devicePermissions.allowed;
    });
  };

  const getDeviceStatus = (device) => {
    if (device.status === "offline") return "offline";
    if (!device.lastSeen) return "offline";

    const lastSeen = new Date(device.lastSeen);
    const now = new Date();
    if (lastSeen > now) return "online";

    const diffMinutes = (now - lastSeen) / 60000;
    const thresholdMin = device.site?.offlineThresholdMinutes || 60;
    const warningMin = thresholdMin === 15 ? 5 : 15;

    if (diffMinutes >= thresholdMin) return "offline";
    if (diffMinutes >= warningMin) return "warning";
    return "online";
  };

  const getTimeSinceLastSeen = (device) => {
    if (!device.lastSeen) return "Nunca";

    const lastSeen = new Date(device.lastSeen);
    const now = new Date();
    const diffMs = now - lastSeen;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return null;
    return path.split(".").reduce((current, key) => {
      return (current !== null && current !== undefined && current[key] !== undefined) ? current[key] : null;
    }, obj);
  };

  const getDeviceValues = (device) => {
    const values = [];

    if (device.magnitudes && device.magnitudes.length > 0) {
      // Filtrar magnitudes que deben mostrarse en dashboard
      let allowedMagnitudes = device.magnitudes.filter(mag => mag.showInDashboard !== false);

      // Filtro 1: Permisos del usuario (admin ve todo)
      if (user && user.role !== "admin" && user.devicePermissions) {
        const deviceId = device._id;
        const devicePermissions = user.devicePermissions[deviceId];

        if (devicePermissions && devicePermissions.magnitudes) {
          allowedMagnitudes = allowedMagnitudes.filter(magnitude => {
            return devicePermissions.magnitudes.includes(magnitude.name);
          });
        } else {
          allowedMagnitudes = [];
        }
      }

      // Filtro 2: Preferencias de visualización del usuario
      // Solo aplica si el usuario ha configurado preferencias para este dispositivo
      const deviceId = device._id;
      const userPrefs = viewPreferences[deviceId];
      if (userPrefs !== undefined && Array.isArray(userPrefs)) {
        // Si el array existe pero está vacío, no mostrar ninguna magnitud
        if (userPrefs.length === 0) {
          allowedMagnitudes = [];
        } else {
          // Si tiene elementos, filtrar solo las seleccionadas
          allowedMagnitudes = allowedMagnitudes.filter(magnitude => {
            return userPrefs.includes(magnitude.name);
          });
        }
      }
      // Si userPrefs es undefined, mostrar todas las magnitudes (comportamiento por defecto)

      allowedMagnitudes.forEach(magnitude => {
        let value = magnitude.currentValue;
        let hasValue = false;

        if (value !== null && value !== undefined) {
          hasValue = true;
        } else {
          if (device.currentState || device.lastData) {
            const data = device.currentState || device.lastData;
            const extractedValue = getNestedValue(data, magnitude.jsonPath);
            if (extractedValue !== null && extractedValue !== undefined) {
              value = extractedValue;
              hasValue = true;
            }
          }
        }

        if (magnitude.valueType === "boolean") {
          let normalizedValue = null;

          if (hasValue) {
            if (typeof value === "boolean") {
              normalizedValue = value;
            } else if (typeof value === "string") {
              const lowerValue = value.toLowerCase();
              if (lowerValue === "true" || lowerValue === "1" || lowerValue === "on" || lowerValue === "yes") {
                normalizedValue = true;
              } else if (lowerValue === "false" || lowerValue === "0" || lowerValue === "off" || lowerValue === "no") {
                normalizedValue = false;
              }
            } else if (typeof value === "number") {
              normalizedValue = value === 1;
            }
          }

          values.push({
            label: magnitude.label,
            value: normalizedValue,
            unit: magnitude.unit,
            decimals: magnitude.decimals,
            minNormal: magnitude.minNormalValue,
            maxNormal: magnitude.maxNormalValue,
            valueType: magnitude.valueType,
            icon: magnitude.icon,
            normalState: magnitude.normalState,
            normalColor: magnitude.normalColor,
            abnormalColor: magnitude.abnormalColor
          });
        } else if (hasValue) {
          values.push({
            label: magnitude.label,
            value: value,
            unit: magnitude.unit,
            decimals: magnitude.decimals,
            minNormal: magnitude.minNormalValue,
            maxNormal: magnitude.maxNormalValue,
            valueType: magnitude.valueType,
            icon: magnitude.icon,
            normalState: magnitude.normalState,
            normalColor: magnitude.normalColor,
            abnormalColor: magnitude.abnormalColor
          });
        }
      });
    }

    return values;
  };

  // Calcular porcentaje del valor en el rango
  const getValuePercentage = (value, min, max) => {
    if (min === null || max === null || min === undefined || max === undefined) return null;
    if (typeof value !== "number") return null;

    const range = max - min;
    if (range === 0) return 50;

    const percentage = ((value - min) / range) * 100;
    return Math.max(0, Math.min(100, percentage));
  };

  // Determinar estado del valor (normal, warning, danger)
  const getValueStatus = (v) => {
    if (v.valueType === "boolean") {
      if (v.value === null || v.value === undefined) return "unknown";
      const isNormalState = v.normalState === "true" ? v.value : !v.value;
      return isNormalState ? "normal" : "danger";
    }

    if (v.valueType !== "numeric" || typeof v.value !== "number") return "normal";

    const hasMin = v.minNormal !== null && v.minNormal !== undefined;
    const hasMax = v.maxNormal !== null && v.maxNormal !== undefined;

    if (!hasMin && !hasMax) return "normal";

    // Calcular umbral de warning (10% del rango)
    if (hasMin && hasMax) {
      const range = v.maxNormal - v.minNormal;
      const warningThreshold = range * 0.1;

      if (v.value < v.minNormal || v.value > v.maxNormal) return "danger";
      if (v.value < v.minNormal + warningThreshold || v.value > v.maxNormal - warningThreshold) return "warning";
      return "normal";
    }

    if (hasMin && v.value < v.minNormal) return "danger";
    if (hasMax && v.value > v.maxNormal) return "danger";

    return "normal";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "normal": return "#10b981";
      case "warning": return "#f59e0b";
      case "danger": return "#ef4444";
      default: return "#6b7280";
    }
  };

  const getVisibleSites = () => {
    if (!Array.isArray(sites)) return [];

    if (!user || user.role === "admin") {
      return sites;
    }

    if (!user.devicePermissions) {
      return [];
    }

    const allowedDeviceIds = Object.entries(user.devicePermissions)
      .filter(([_, perm]) => perm && perm.allowed)
      .map(([deviceId, _]) => deviceId);

    return sites.filter(site => {
      const siteDevices = Array.isArray(devices)
        ? devices.filter(d => d.site?._id === site._id || d.siteId === site._id)
        : [];

      return siteDevices.some(device => allowedDeviceIds.includes(device._id));
    });
  };

  const visibleSites = getVisibleSites();
  const filteredSites = visibleSites.filter(site =>
    site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    // Ordenar sitios: los que tienen dispositivos online primero
    const aDevices = devices.filter(d => d.site?._id === a._id || d.siteId === a._id);
    const bDevices = devices.filter(d => d.site?._id === b._id || d.siteId === b._id);
    const aOnline = aDevices.filter(d => getDeviceStatus(d) === "online").length;
    const bOnline = bDevices.filter(d => getDeviceStatus(d) === "online").length;
    // Sitios con más dispositivos online primero
    if (bOnline !== aOnline) return bOnline - aOnline;
    // Si igual cantidad online, ordenar por nombre
    return a.name.localeCompare(b.name);
  });

  const getVisibleDevices = () => {
    if (!Array.isArray(devices)) return [];

    if (!user || user.role === "admin") {
      return devices;
    }

    if (!user.devicePermissions) {
      return [];
    }

    return devices.filter(device => {
      const deviceId = device._id;
      const devicePermissions = user.devicePermissions[deviceId];
      return devicePermissions && devicePermissions.allowed;
    });
  };

  const visibleDevices = getVisibleDevices();
  const totalDevices = visibleDevices.length;
  const onlineDevices = visibleDevices.filter(d => getDeviceStatus(d) === "online").length;
  const offlineDevices = totalDevices - onlineDevices;

  if (loading) {
    return (
      <div style={{
        padding: "1rem",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
      }}>
        <div style={{ color: "white" }}>
          <div style={{
            width: "50px",
            height: "50px",
            border: "3px solid rgba(255,255,255,0.3)",
            borderTop: "3px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 1rem"
          }} />
          Cargando dashboard...
        </div>
      </div>
    );
  }

  if (showAlertsOnly) {
    return <CriticalAlerts devices={devices} sites={sites} user={user} />;
  }

  // Componente de tarjeta de dispositivo con glassmorphism
  const DeviceCard = ({ device }) => {
    const status = getDeviceStatus(device);
    const isOnline = status === "online";
    const isWarning = status === "warning";
    const showAsActive = isOnline || isWarning;
    const palette = {
      online:  { primary: "#10b981", border: "rgba(16, 185, 129, 0.3)", borderWidth: 1, glow: "rgba(16, 185, 129, 0.15)", glowHover: "rgba(16, 185, 129, 0.25)", badgeBg: "rgba(16, 185, 129, 0.25)", badgeBorder: "rgba(16, 185, 129, 0.5)", dotGlow: "rgba(16, 185, 129, 0.8)" },
      warning: { primary: "#f59e0b", border: "rgba(245, 158, 11, 0.6)", borderWidth: 2, glow: "rgba(245, 158, 11, 0.25)", glowHover: "rgba(245, 158, 11, 0.4)",  badgeBg: "rgba(245, 158, 11, 0.25)", badgeBorder: "rgba(245, 158, 11, 0.6)", dotGlow: "rgba(245, 158, 11, 0.8)" },
      offline: { primary: "#ef4444", border: "rgba(239, 68, 68, 0.5)",  borderWidth: 2, glow: "rgba(239, 68, 68, 0.3)",   glowHover: "rgba(239, 68, 68, 0.4)",   badgeBg: "rgba(239, 68, 68, 0.25)",  badgeBorder: "rgba(239, 68, 68, 0.5)",  dotGlow: "rgba(239, 68, 68, 0.8)"  }
    };
    const c = palette[status] || palette.offline;
    const timeSince = getTimeSinceLastSeen(device);
    const values = getDeviceValues(device);

    return (
      <div style={{
        background: showAsActive ? themeColors.onlineGradient : themeColors.offlineGradient,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: "16px",
        border: `${c.borderWidth}px solid ${c.border}`,
        padding: "1rem",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        boxShadow: `0 8px 32px ${c.glow}, inset 0 1px 0 rgba(255,255,255,0.1)`
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = `0 16px 48px ${c.glowHover}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = `0 8px 32px ${c.glow}`;
      }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "0.75rem"
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{
              margin: 0,
              fontSize: "0.95rem",
              fontWeight: "700",
              color: themeColors.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textShadow: theme === "dark" ? "0 2px 4px rgba(0,0,0,0.3)" : "none"
            }}>
              {device.name}
            </h4>
            <span style={{
              fontSize: "0.7rem",
              color: themeColors.textSecondary,
              display: "block",
              marginTop: "0.125rem"
            }}>
              {device.deviceId}
            </span>
          </div>

          {/* Status Badge */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            padding: "0.25rem 0.625rem",
            borderRadius: "20px",
            background: c.badgeBg,
            border: `1px solid ${c.badgeBorder}`,
            backdropFilter: "blur(10px)"
          }}>
            <div style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: c.primary,
              boxShadow: `0 0 12px ${c.dotGlow}`,
              animation: showAsActive ? "pulse 2s ease-in-out infinite" : "none"
            }} />
            <span style={{
              fontSize: "0.7rem",
              fontWeight: "600",
              color: c.primary
            }}>
              {timeSince}
            </span>
          </div>
        </div>

        {/* Valores */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem"
        }}>
          {values.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "1rem",
              color: themeColors.textMuted,
              fontSize: "0.8rem"
            }}>
              Sin datos
            </div>
          ) : (
            values.map((v, idx) => {
              const valueStatus = getValueStatus(v);
              const statusColor = getStatusColor(valueStatus);
              const percentage = getValuePercentage(v.value, v.minNormal, v.maxNormal);

              return (
                <div key={idx} style={{
                  background: themeColors.valueBg,
                  borderRadius: "10px",
                  padding: "0.625rem",
                  border: `1px solid ${themeColors.border}`
                }}>
                  {/* Label y valor */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: percentage !== null ? "0.375rem" : 0
                  }}>
                    <span style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.375rem",
                      fontSize: "0.75rem",
                      color: themeColors.textSecondary
                    }}>
                      {v.icon && <span>{v.icon}</span>}
                      {v.label}
                    </span>

                    <span style={{
                      fontWeight: "700",
                      fontSize: v.valueType === "boolean" ? "0.8rem" : "1rem",
                      color: statusColor,
                      textShadow: `0 0 20px ${statusColor}40`
                    }}>
                      {v.valueType === "boolean" ? (
                        v.value === null || v.value === undefined ? (
                          <span style={{ color: "#6b7280" }}>???</span>
                        ) : (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem"
                          }}>
                            <span style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: statusColor,
                              boxShadow: `0 0 10px ${statusColor}`
                            }} />
                            {v.value ? "ON" : "OFF"}
                          </span>
                        )
                      ) : v.valueType === "text" ? (
                        String(v.value)
                      ) : typeof v.value === "number" ? (
                        `${v.value.toFixed(v.decimals || 1)}${v.unit || ""}`
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>

                  {/* Barra de rango para valores numéricos */}
                  {percentage !== null && v.valueType === "numeric" && (
                    <div style={{ position: "relative" }}>
                      {/* Barra de fondo */}
                      <div style={{
                        height: "6px",
                        background: "rgba(255,255,255,0.1)",
                        borderRadius: "3px",
                        overflow: "hidden",
                        position: "relative"
                      }}>
                        {/* Zona normal (verde) */}
                        <div style={{
                          position: "absolute",
                          left: "10%",
                          right: "10%",
                          top: 0,
                          bottom: 0,
                          background: "rgba(16, 185, 129, 0.3)",
                          borderRadius: "3px"
                        }} />

                        {/* Indicador de posición */}
                        <div style={{
                          position: "absolute",
                          left: `${percentage}%`,
                          top: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          background: statusColor,
                          border: "2px solid rgba(255,255,255,0.9)",
                          boxShadow: `0 0 10px ${statusColor}, 0 2px 4px rgba(0,0,0,0.3)`,
                          transition: "left 0.5s ease-out"
                        }} />
                      </div>

                      {/* Min/Max labels */}
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "0.25rem",
                        fontSize: "0.6rem",
                        color: "rgba(255,255,255,0.4)"
                      }}>
                        <span>{v.minNormal}{v.unit || ""}</span>
                        <span>{v.maxNormal}{v.unit || ""}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}

        </div>

        {/* Botones de acción */}
        <div style={{
          display: "flex",
          gap: "0.5rem",
          marginTop: "0.75rem"
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openGaugePanel(device);
            }}
            style={{
              flex: 1,
              padding: "0.5rem",
              background: "linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(139, 92, 246, 0.1) 100%)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              borderRadius: "8px",
              color: "#a78bfa",
              fontSize: "0.75rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.25rem",
              transition: "all 0.2s ease",
              backdropFilter: "blur(10px)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(139, 92, 246, 0.5) 0%, rgba(139, 92, 246, 0.2) 100%)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(139, 92, 246, 0.1) 100%)";
            }}
          >
            🎛️ Panel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openHistory(device._id);
            }}
            style={{
              flex: 1,
              padding: "0.5rem",
              background: "linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.1) 100%)",
              border: "1px solid rgba(59, 130, 246, 0.4)",
              borderRadius: "8px",
              color: "#60a5fa",
              fontSize: "0.75rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.25rem",
              transition: "all 0.2s ease",
              backdropFilter: "blur(10px)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(59, 130, 246, 0.2) 100%)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.1) 100%)";
            }}
          >
            📊 Historial
          </button>
        </div>
      </div>
    );
  };

  // Componente de fila de dispositivo para vista lista
  const DeviceRow = ({ device }) => {
    const status = getDeviceStatus(device);
    const isOnline = status === "online";
    const isWarning = status === "warning";
    const showAsActive = isOnline || isWarning;
    const rowAccent = isWarning
      ? { primary: "#f59e0b", border: "rgba(245, 158, 11, 0.6)", borderWidth: 2, glow: "rgba(245, 158, 11, 0.25)", dotGlow: "rgba(245, 158, 11, 0.6)" }
      : isOnline
        ? { primary: "#10b981", border: "rgba(16, 185, 129, 0.2)", borderWidth: 1, glow: null,                       dotGlow: "rgba(16, 185, 129, 0.6)" }
        : { primary: "#ef4444", border: "rgba(239, 68, 68, 0.5)",  borderWidth: 2, glow: "rgba(239, 68, 68, 0.25)",  dotGlow: "rgba(239, 68, 68, 0.6)"  };
    const timeSince = getTimeSinceLastSeen(device);
    const values = getDeviceValues(device);

    return (
      <div style={{
        background: showAsActive ? themeColors.onlineRowGradient : themeColors.offlineRowGradient,
        backdropFilter: "blur(10px)",
        borderRadius: "12px",
        border: `${rowAccent.borderWidth}px solid ${rowAccent.border}`,
        padding: "0.75rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        transition: "all 0.2s ease",
        boxShadow: rowAccent.glow ? `0 4px 16px ${rowAccent.glow}` : "none"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateX(4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateX(0)";
      }}
      >
        {/* Status indicator */}
        <div style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: rowAccent.primary,
          boxShadow: `0 0 12px ${rowAccent.dotGlow}`,
          animation: showAsActive ? "pulse 2s ease-in-out infinite" : "none",
          flexShrink: 0
        }} />

        {/* Device info */}
        <div style={{ minWidth: "150px", flexShrink: 0 }}>
          <div style={{
            fontWeight: "600",
            fontSize: "0.9rem",
            color: themeColors.text
          }}>
            {device.name}
          </div>
          <div style={{
            fontSize: "0.7rem",
            color: themeColors.textMuted
          }}>
            {timeSince}
          </div>
        </div>

        {/* Values */}
        <div style={{
          flex: 1,
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          justifyContent: "flex-start"
        }}>
          {values.map((v, idx) => {
            const valueStatus = getValueStatus(v);
            const statusColor = getStatusColor(valueStatus);

            return (
              <div key={idx} style={{
                background: themeColors.valueBg,
                borderRadius: "8px",
                padding: "0.375rem 0.625rem",
                border: `1px solid ${statusColor}30`,
                display: "flex",
                alignItems: "center",
                gap: "0.375rem"
              }}>
                {v.icon && <span style={{ fontSize: "0.8rem" }}>{v.icon}</span>}
                <span style={{
                  fontSize: "0.7rem",
                  color: themeColors.textSecondary,
                  maxWidth: "60px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                  {v.label}:
                </span>
                <span style={{
                  fontWeight: "700",
                  fontSize: "0.85rem",
                  color: statusColor
                }}>
                  {v.valueType === "boolean" ? (
                    v.value === null ? "???" : (v.value ? "ON" : "OFF")
                  ) : v.valueType === "text" ? (
                    String(v.value).substring(0, 10)
                  ) : typeof v.value === "number" ? (
                    `${v.value.toFixed(v.decimals || 1)}${v.unit || ""}`
                  ) : "-"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openGaugePanel(device);
            }}
            style={{
              padding: "0.5rem 0.75rem",
              background: "linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(139, 92, 246, 0.1) 100%)",
              border: "1px solid rgba(139, 92, 246, 0.4)",
              borderRadius: "8px",
              color: "#a78bfa",
              fontSize: "0.75rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              transition: "all 0.2s ease"
            }}
          >
            🎛️ Panel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openHistory(device._id);
            }}
            style={{
              padding: "0.5rem 0.75rem",
              background: "linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0.1) 100%)",
              border: "1px solid rgba(59, 130, 246, 0.4)",
              borderRadius: "8px",
              color: "#60a5fa",
              fontSize: "0.75rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              transition: "all 0.2s ease"
            }}
          >
            📊 Historial
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: themeColors.background,
      padding: "1rem",
      paddingBottom: "4rem",
      transition: "background 0.3s ease"
    }}>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.1);
            }
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .site-card {
            animation: fadeIn 0.3s ease-out;
          }
        `}
      </style>

      {/* Header */}
      <div style={{
        background: themeColors.headerBg,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: "16px",
        border: `1px solid ${themeColors.border}`,
        padding: "1rem 1.25rem",
        marginBottom: "1rem",
        boxShadow: themeColors.shadow,
        transition: "all 0.3s ease"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
          gap: "0.75rem"
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: "1.5rem",
              fontWeight: "700",
              color: themeColors.text,
              textShadow: theme === "dark" ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
              transition: "color 0.3s ease"
            }}>
              Dashboard IoT
            </h1>
            <p style={{
              margin: "0.25rem 0 0",
              fontSize: "0.75rem",
              color: themeColors.textSecondary,
              transition: "color 0.3s ease"
            }}>
              {user?.username} • {user?.role}
            </p>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              background: "rgba(16, 185, 129, 0.15)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              borderRadius: "12px",
              backdropFilter: "blur(10px)"
            }}>
              <div style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: "#10b981",
                boxShadow: "0 0 10px rgba(16, 185, 129, 0.6)",
                animation: "pulse 2s ease-in-out infinite"
              }} />
              <span style={{ fontWeight: "700", fontSize: "1.1rem", color: "#10b981" }}>
                {onlineDevices}
              </span>
              <span style={{ fontSize: "0.75rem", color: themeColors.textSecondary }}>online</span>
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              background: "rgba(239, 68, 68, 0.15)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "12px",
              backdropFilter: "blur(10px)"
            }}>
              <div style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: "#ef4444",
                boxShadow: "0 0 10px rgba(239, 68, 68, 0.6)"
              }} />
              <span style={{ fontWeight: "700", fontSize: "1.1rem", color: "#ef4444" }}>
                {offlineDevices}
              </span>
              <span style={{ fontSize: "0.75rem", color: themeColors.textSecondary }}>offline</span>
            </div>

            {/* Global Actions */}
            <button
              onClick={() => setShowGlobalControlPanel(true)}
              style={{
                padding: "0.5rem 1rem",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                border: "none",
                borderRadius: "10px",
                color: "white",
                fontSize: "0.8rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                boxShadow: "0 4px 15px rgba(16, 185, 129, 0.4)",
                transition: "all 0.2s ease"
              }}
            >
              🎛️ Panel Global
            </button>

            <button
              onClick={() => window.open('/global-history', '_blank')}
              style={{
                padding: "0.5rem 1rem",
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                border: "none",
                borderRadius: "10px",
                color: "white",
                fontSize: "0.8rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                transition: "all 0.2s ease"
              }}
            >
              📊 Historial Global
            </button>

            {/* Theme Toggle */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              style={{
                padding: "0.5rem 1rem",
                background: theme === "dark"
                  ? "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)"
                  : "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                border: "none",
                borderRadius: "10px",
                color: "white",
                fontSize: "0.8rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                boxShadow: theme === "dark"
                  ? "0 4px 15px rgba(251, 191, 36, 0.4)"
                  : "0 4px 15px rgba(30, 41, 59, 0.4)",
                transition: "all 0.2s ease"
              }}
              title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            >
              {theme === "dark" ? "☀️ Claro" : "🌙 Oscuro"}
            </button>
          </div>
        </div>

        {/* Search & Controls */}
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <input
              type="text"
              placeholder="🔍 Buscar sitios o dispositivos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "0.625rem 1rem",
                background: themeColors.inputBg,
                border: `1px solid ${themeColors.border}`,
                borderRadius: "10px",
                fontSize: "0.875rem",
                color: themeColors.text,
                outline: "none",
                transition: "all 0.2s ease"
              }}
            />
          </div>

          {/* Selector de vista */}
          <div style={{
            display: "flex",
            background: themeColors.buttonBg,
            borderRadius: "10px",
            border: `1px solid ${themeColors.border}`,
            padding: "0.25rem",
            gap: "0.25rem"
          }}>
            <button
              onClick={() => setViewMode("cards")}
              style={{
                padding: "0.5rem 0.75rem",
                background: viewMode === "cards" ? "rgba(99, 102, 241, 0.3)" : "transparent",
                border: viewMode === "cards" ? "1px solid rgba(99, 102, 241, 0.5)" : "1px solid transparent",
                borderRadius: "8px",
                color: viewMode === "cards" ? "#818cf8" : themeColors.textSecondary,
                fontSize: "0.8rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem"
              }}
              title="Vista de tarjetas"
            >
              📱 Cards
            </button>
            <button
              onClick={() => setViewMode("list")}
              style={{
                padding: "0.5rem 0.75rem",
                background: viewMode === "list" ? "rgba(99, 102, 241, 0.3)" : "transparent",
                border: viewMode === "list" ? "1px solid rgba(99, 102, 241, 0.5)" : "1px solid transparent",
                borderRadius: "8px",
                color: viewMode === "list" ? "#818cf8" : themeColors.textSecondary,
                fontSize: "0.8rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem"
              }}
              title="Vista de lista"
            >
              📋 Lista
            </button>
            <button
              onClick={() => setViewMode("metrics")}
              style={{
                padding: "0.5rem 0.75rem",
                background: viewMode === "metrics" ? "rgba(99, 102, 241, 0.3)" : "transparent",
                border: viewMode === "metrics" ? "1px solid rgba(99, 102, 241, 0.5)" : "1px solid transparent",
                borderRadius: "8px",
                color: viewMode === "metrics" ? "#818cf8" : themeColors.textSecondary,
                fontSize: "0.8rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem"
              }}
              title="Vista de métricas"
            >
              📊 Métricas
            </button>
          </div>

          <button
            onClick={expandAll}
            style={{
              padding: "0.625rem",
              background: "rgba(16, 185, 129, 0.2)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              borderRadius: "10px",
              color: "#10b981",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
            title="Expandir todo"
          >
            ▼
          </button>

          <button
            onClick={collapseAll}
            style={{
              padding: "0.625rem",
              background: "rgba(239, 68, 68, 0.2)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "10px",
              color: "#ef4444",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
            title="Colapsar todo"
          >
            ▶
          </button>
        </div>
      </div>

      {/* Metrics View */}
      {viewMode === "metrics" ? (
        <MetricsView
          devices={devices}
          sites={sites}
          user={user}
          viewPreferences={viewPreferences}
          onOpenHistory={openHistory}
          onOpenGaugePanel={openGaugePanel}
          getDeviceStatus={getDeviceStatus}
          getDeviceValues={getDeviceValues}
          getValueStatus={getValueStatus}
          theme={theme}
          themeColors={themeColors}
        />
      ) : (
        /* Sites - Cards & List Views */
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {filteredSites.length === 0 ? (
            <div style={{
              background: themeColors.buttonBg,
              backdropFilter: "blur(20px)",
              borderRadius: "16px",
              border: `1px solid ${themeColors.border}`,
              padding: "3rem",
              textAlign: "center"
            }}>
              <p style={{ color: themeColors.textSecondary, fontSize: "1rem" }}>
                {searchTerm ? "No se encontraron sitios" : "No hay sitios asignados"}
              </p>
            </div>
          ) : (
            filteredSites.map(site => {
            const siteDevices = getDevicesBysite(site._id);
            const onlineCount = siteDevices.filter(d => getDeviceStatus(d) === "online").length;

            return (
              <div
                key={site._id}
                className="site-card"
                style={{
                  background: themeColors.cardBg,
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  borderRadius: "16px",
                  border: `1px solid ${themeColors.border}`,
                  overflow: "hidden",
                  boxShadow: themeColors.shadow,
                  transition: "all 0.3s ease"
                }}
              >
                {/* Site Header */}
                <div
                  onClick={() => toggleSiteExpansion(site._id)}
                  style={{
                    padding: "1rem 1.25rem",
                    background: themeColors.siteHeaderBg,
                    borderBottom: expandedSites[site._id] ? `1px solid ${themeColors.border}` : "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <span style={{
                      fontSize: "0.75rem",
                      color: themeColors.textSecondary,
                      transition: "transform 0.3s ease",
                      transform: expandedSites[site._id] ? "rotate(0deg)" : "rotate(-90deg)"
                    }}>
                      ▼
                    </span>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: "1.1rem",
                          fontWeight: "600",
                          color: themeColors.text
                        }}>
                          {site.name}
                        </h3>
                        {(site.telegramBots?.length > 0
                          ? site.telegramBots.some(bot => bot.enabled)
                          : site.telegramConfig?.enabled) && (
                          <button
                            onClick={(e) => sendSiteStatusToTelegram(site._id, site.name, e)}
                            title="Enviar estado a Telegram"
                            style={{
                              background: "rgba(59, 130, 246, 0.3)",
                              border: "1px solid rgba(59, 130, 246, 0.5)",
                              borderRadius: "6px",
                              padding: "0.25rem 0.5rem",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              fontSize: "0.7rem",
                              color: "#93c5fd",
                              transition: "all 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "rgba(59, 130, 246, 0.5)";
                              e.target.style.transform = "scale(1.05)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "rgba(59, 130, 246, 0.3)";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            📲
                          </button>
                        )}
                        {site.location?.coordinates?.lat && site.location?.coordinates?.lng && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://www.google.com/maps?q=${site.location.coordinates.lat},${site.location.coordinates.lng}&z=15`, '_blank', 'noopener,noreferrer');
                            }}
                            title="Ver ubicación en Google Maps"
                            style={{
                              background: "rgba(34, 197, 94, 0.3)",
                              border: "1px solid rgba(34, 197, 94, 0.5)",
                              borderRadius: "6px",
                              padding: "0.25rem 0.5rem",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              fontSize: "0.7rem",
                              color: "#86efac",
                              transition: "all 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "rgba(34, 197, 94, 0.5)";
                              e.target.style.transform = "scale(1.05)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "rgba(34, 197, 94, 0.3)";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            🗺️
                          </button>
                        )}
                      </div>
                      {site.description && (
                        <p style={{
                          margin: "0.125rem 0 0",
                          fontSize: "0.75rem",
                          color: themeColors.textMuted
                        }}>
                          {site.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.375rem 0.75rem",
                    background: onlineCount > 0
                      ? "rgba(16, 185, 129, 0.2)"
                      : "rgba(239, 68, 68, 0.2)",
                    border: `1px solid ${onlineCount > 0 ? "rgba(16, 185, 129, 0.4)" : "rgba(239, 68, 68, 0.4)"}`,
                    borderRadius: "20px"
                  }}>
                    <span style={{
                      fontWeight: "700",
                      fontSize: "0.9rem",
                      color: onlineCount > 0 ? "#10b981" : "#ef4444"
                    }}>
                      {onlineCount}/{siteDevices.length}
                    </span>
                    <div style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: onlineCount > 0 ? "#10b981" : "#ef4444",
                      boxShadow: `0 0 10px ${onlineCount > 0 ? "rgba(16, 185, 129, 0.6)" : "rgba(239, 68, 68, 0.6)"}`
                    }} />
                  </div>
                </div>

                {/* Site Content */}
                {expandedSites[site._id] && (
                  <div style={{ padding: "1rem" }}>
                    {siteDevices.length === 0 ? (
                      <p style={{
                        textAlign: "center",
                        color: themeColors.textMuted,
                        padding: "2rem",
                        fontSize: "0.875rem"
                      }}>
                        No hay dispositivos en este sitio
                      </p>
                    ) : viewMode === "cards" ? (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: "1rem"
                      }}>
                        {siteDevices.map(device => (
                          <DeviceCard key={device._id} device={device} />
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {siteDevices.map(device => (
                          <DeviceRow key={device._id} device={device} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>
      )}

      {/* Modals */}
      {showHistory && (
        <DeviceHistory
          deviceId={selectedDeviceId}
          user={user}
          viewPreferences={viewPreferences}
          onClose={() => setShowHistory(false)}
        />
      )}

      {showGlobalHistory && (
        <GlobalHistory
          devices={devices}
          sites={sites}
          user={user}
          viewPreferences={viewPreferences}
          onClose={() => setShowGlobalHistory(false)}
        />
      )}

      {showGaugePanel && selectedDevice && (
        <GaugePanel
          device={selectedDevice}
          user={user}
          viewPreferences={viewPreferences}
          commandPreferences={commandPreferences}
          onClose={() => setShowGaugePanel(false)}
        />
      )}

      {showGlobalControlPanel && (
        <GlobalControlPanel
          devices={devices}
          sites={sites}
          user={user}
          viewPreferences={viewPreferences}
          commandPreferences={commandPreferences}
          onClose={() => setShowGlobalControlPanel(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
